/**
 * src/components/layout/NotificationsMenu.tsx
 * --------------------------------------------
 * The top-bar bell. Previously the button had no handler and its red dot was
 * hardcoded, so it always looked like there was something to read and never
 * opened anything.
 *
 * Now it shows the real alert feed: the unread count drives the dot, the panel
 * lists recent alert events from GET /alerts/events, and opening it marks them
 * read (the same call the Alerts view makes) so the badge clears.
 *
 * Shares the `['alerts','unread-count']` query key with the Sidebar badge and
 * AlertNotifier, so all three stay consistent without extra requests.
 */
import { useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ROUTES } from '@shared/constants'
import type { Alert } from '@shared/types'
import { listAlerts, markAlertsRead } from '@/api/alerts'
import { ALERTS_UNREAD_KEY, useAlertsUnread } from '@/hooks/useAlertsUnread'
import { Icon } from '@/components/ui/Icon'

const SEVERITY_TONE: Record<string, string> = {
  critical: 'var(--err)',
  error: 'var(--err)',
  warning: 'var(--warn)',
  info: 'var(--info)',
}

function relativeTime(iso?: string): string {
  if (!iso) return ''
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return ''
  const seconds = Math.max(0, Math.round((Date.now() - then) / 1000))
  if (seconds < 60) return 'just now'
  const minutes = Math.round(seconds / 60)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.round(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  return `${Math.round(hours / 24)}d ago`
}

interface NotificationsMenuProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function NotificationsMenu({ open, onOpenChange }: NotificationsMenuProps) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { data: unread = 0 } = useAlertsUnread()
  const wrapRef = useRef<HTMLDivElement>(null)

  const { data, isLoading, isError } = useQuery({
    queryKey: ['alerts', 'recent'],
    queryFn: () => listAlerts({ page: 1, page_size: 8 }),
    enabled: open,
    staleTime: 10_000,
  })

  const markRead = useMutation({
    mutationFn: markAlertsRead,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ALERTS_UNREAD_KEY }),
  })

  // Opening the panel is the "I've seen these" signal, matching the Alerts view.
  const markReadRef = useRef(markRead)
  markReadRef.current = markRead
  useEffect(() => {
    if (open && unread > 0) markReadRef.current.mutate()
  }, [open, unread])

  // Dismiss on outside click and on Escape.
  useEffect(() => {
    if (!open) return
    function onPointerDown(e: MouseEvent) {
      if (!wrapRef.current?.contains(e.target as Node)) onOpenChange(false)
    }
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') onOpenChange(false)
    }
    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open, onOpenChange])

  const alerts: Alert[] = data?.items ?? []

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <button
        className="btn ghost icon"
        onClick={() => onOpenChange(!open)}
        data-tip="Notifications"
        aria-label={unread > 0 ? `Notifications, ${unread} unread` : 'Notifications'}
        aria-expanded={open}
        aria-haspopup="dialog"
        style={{ position: 'relative' }}
      >
        <Icon name="bell" size={14} />
        {unread > 0 && (
          <span
            aria-hidden
            style={{
              position: 'absolute',
              top: 4,
              right: 4,
              minWidth: 15,
              height: 15,
              padding: '0 4px',
              borderRadius: 99,
              background: 'var(--err)',
              color: 'white',
              fontSize: 9.5,
              fontWeight: 700,
              lineHeight: '15px',
              textAlign: 'center',
            }}
          >
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          role="dialog"
          aria-label="Notifications"
          style={{
            position: 'absolute',
            top: 'calc(100% + 10px)',
            right: 0,
            width: 348,
            maxHeight: 420,
            display: 'flex',
            flexDirection: 'column',
            background: 'var(--bg-elev)',
            border: '1px solid var(--line)',
            borderRadius: 'var(--r-md)',
            boxShadow: 'var(--sh-3)',
            zIndex: 60,
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              padding: '11px 14px',
              borderBottom: '1px solid var(--line)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <strong style={{ fontSize: 13, color: 'var(--ink)' }}>Notifications</strong>
            <button
              className="btn ghost sm"
              onClick={() => {
                onOpenChange(false)
                navigate(ROUTES.ALERTS)
              }}
            >
              View all
            </button>
          </div>

          <div style={{ overflowY: 'auto', flex: 1 }}>
            {isLoading && (
              <p style={{ padding: '18px 14px', fontSize: 12.5, color: 'var(--ink-3)', margin: 0 }}>
                Loading…
              </p>
            )}

            {isError && (
              <p style={{ padding: '18px 14px', fontSize: 12.5, color: 'var(--err)', margin: 0 }}>
                Couldn’t load alerts.
              </p>
            )}

            {!isLoading && !isError && alerts.length === 0 && (
              <p style={{ padding: '22px 14px', fontSize: 12.5, color: 'var(--ink-3)', margin: 0 }}>
                Nothing to review. New alerts appear here as they fire.
              </p>
            )}

            {alerts.map((alert) => (
              <button
                key={alert.id}
                onClick={() => {
                  onOpenChange(false)
                  navigate(ROUTES.ALERTS)
                }}
                style={{
                  width: '100%',
                  display: 'flex',
                  gap: 10,
                  padding: '11px 14px',
                  border: 0,
                  borderBottom: '1px solid var(--line)',
                  background: 'transparent',
                  textAlign: 'left',
                  cursor: 'pointer',
                }}
              >
                <span
                  aria-hidden
                  style={{
                    marginTop: 5,
                    width: 7,
                    height: 7,
                    borderRadius: 99,
                    flexShrink: 0,
                    background: SEVERITY_TONE[alert.severity] ?? 'var(--ink-4)',
                  }}
                />
                <span style={{ minWidth: 0, flex: 1 }}>
                  <span
                    style={{
                      display: 'block',
                      fontSize: 12.5,
                      fontWeight: 600,
                      color: 'var(--ink)',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {alert.title}
                  </span>
                  {alert.description && (
                    <span
                      style={{
                        display: 'block',
                        marginTop: 2,
                        fontSize: 11.5,
                        color: 'var(--ink-3)',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {alert.description}
                    </span>
                  )}
                  <span style={{ display: 'block', marginTop: 3, fontSize: 10.5, color: 'var(--ink-4)' }}>
                    {relativeTime(alert.triggered_at)}
                  </span>
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
